import { api, req, type GatewaySettings } from "../api.ts";
import { esc, h } from "../dom.ts";
import { getLang, t } from "../i18n/index.ts";
import { readPlayer } from "../play/client.ts";
import { rememberReturn } from "../returnContext.ts";
import { mountedSubscription } from "../ui/lifecycle.ts";
import { emailSignIn } from "./emailSignIn.ts";

type Link = { deep_link: string; expires_at: number };

export function telegramSettings(connect: boolean): HTMLElement {
  const root = h('<section class="telegram-settings" hidden></section>');
  let dead = false, busy = false, refreshing = false;
  let timer: number | undefined;
  let settings: GatewaySettings | undefined;
  let pending: Link | undefined;
  let notice = "";
  const stop = () => { if (timer) clearInterval(timer); timer = undefined; };
  function paint(): void {
    if (dead || !settings) return;
    const tg = settings.telegram;
    root.hidden = !connect && !tg.available && !tg.connected;
    if (root.hidden) return;
    root.innerHTML = `<h3>${t("telegram.title")}</h3><p>${t(tg.connected ? "telegram.connected" : "telegram.intro")}</p><div data-controls></div><p class="msg" role="status">${esc(notice)}</p>`;
    const controls = root.querySelector<HTMLElement>("[data-controls]")!;
    if (tg.connected) {
      pending = undefined; stop();
      if (!tg.available) controls.append(h(`<p class="fine">${t("telegram.unavailable")}</p>`));
      const toggle = h(`<label class="field field-toggle"><span>${t("telegram.alerts")}</span><input type="checkbox" name="telegram" ${tg.enabled ? "checked" : ""} ${!tg.available ? "disabled" : ""} /></label>`);
      toggle.querySelector("input")!.onchange = () => {
        const enabled = toggle.querySelector("input")!.checked;
        void action(async () => { await api.updateNotificationPreferences(enabled, getLang()); notice = t("settings.saved"); await refresh(); });
      };
      controls.append(toggle);
      button(controls, t("telegram.disconnect"), () => void action(async () => {
        await api.unlinkGateway("telegram"); notice = t("telegram.disconnected"); await refresh();
      }));
    } else if (!tg.available) {
      controls.append(h(`<p>${t("telegram.unavailable")}</p>`));
      button(controls,t("common.retry"),()=>void refresh());
    } else if (pending) {
      const expired = pending.expires_at * 1000 <= Date.now();
      if (expired) { stop(); pending = undefined; notice = t("telegram.expired"); paint(); return; }
      controls.append(h(`<div><a class="btn primary" href="${esc(pending.deep_link)}" target="_blank" rel="noopener">${t("settings.openTelegram")}</a><p>${t("telegram.waiting")}</p><p class="fine">${t("telegram.pressStart")}</p></div>`));
      button(controls, t("common.cancel"), () => void action(async () => {
        await req("DELETE", "/api/gateway/bindings/telegram/link");
        pending = undefined; stop(); notice = ""; await refresh();
      }));
    } else {
      const retry = ["telegram.expired", "telegram.failed", "telegram.statusFailed"].some(key => notice === t(key));
      button(controls, t(retry ? "common.retry" : "telegram.connect"), () => void action(issue));
    }
    root.querySelectorAll<HTMLButtonElement | HTMLInputElement>("button,input").forEach(el => { if (busy) el.disabled = true; });
  }
  function button(parent: HTMLElement, label: string, run: () => void): void {
    const el = document.createElement("button"); el.className = "btn"; el.type = "button"; el.textContent = label;
    el.onclick = run; parent.append(el);
  }
  async function action(run: () => Promise<void>): Promise<void> {
    if (busy || dead) return; busy = true; paint();
    try { await run(); } catch { notice = t("telegram.failed"); }
    finally { busy = false; paint(); }
  }
  async function issue(): Promise<void> {
    const result = await api.requestGatewayLink("telegram", getLang());
    if (dead) return;
    if (!result.deep_link) throw new Error("No usable link");
    pending = result; notice = ""; stop();
    timer = window.setInterval(() => {
      if (!pending || pending.expires_at * 1000 <= Date.now()) { paint(); stop(); }
      else void refresh();
    }, 2500);
  }
  async function refresh(): Promise<void> {
    if (dead || refreshing) return;
    refreshing = true;
    try {
      const next = await api.gatewaySettings();
      const changed = JSON.stringify(next) !== JSON.stringify(settings);
      const recovered = notice === t("telegram.statusFailed");
      if (recovered) notice = "";
      settings = next;
      if (!dead && (changed || recovered || !pending || pending.expires_at * 1000 <= Date.now())) paint();
    }
    catch { if (!dead) {
      notice = t("telegram.statusFailed");
      if (settings) paint();
      else { root.hidden=false;root.replaceChildren(h(`<h3>${t("telegram.title")}</h3><p class="msg">${notice}</p>`));button(root,t("common.retry"),()=>void load()); }
    } }
    finally { refreshing = false; }
  }
  async function load(): Promise<void> {
    try {
      const player = await readPlayer();
      if (dead) return;
      if (player?.kind === "account") {
        await refresh();
        if (connect && settings?.telegram.available && !settings.telegram.connected) await action(issue);
        if (connect && !dead) root.scrollIntoView({block: "start"});
      } else {
        const config = await api.getConfig();
        if (dead) return;
        if (!config.telegram_available) {
          if (connect) {root.hidden=false;root.textContent=t("telegram.unavailable");button(root,t("common.retry"),()=>void load());}
          return;
        }
        root.hidden = false;
        root.innerHTML = `<h3>${t("telegram.title")}</h3><p>${t("telegram.intro")}</p><p class="fine">${t("telegram.accountRequired")}</p><div data-connect></div>`;
        const box = root.querySelector<HTMLElement>("[data-connect]")!;
        const signIn = () => {
          rememberReturn("/connect/telegram");
          box.replaceChildren(config.email_signin_enabled ? emailSignIn(config.registration_mode === "public") : h(`<p class="msg">${t("library.emailDisabled")}</p>`));
        };
        if (connect) { signIn(); root.scrollIntoView({block: "start"}); }
        else button(box, t("telegram.connect"), signIn);
      }
    } catch {
      if (connect && !dead) {
        root.hidden = false; root.textContent = t("telegram.statusFailed");
        button(root, t("common.retry"), () => void load());
      }
    }
  }
  mountedSubscription(root, () => {
    const focus = () => { if (pending || settings) void refresh(); };
    window.addEventListener("focus", focus);
    const visible = () => { if (document.visibilityState === "visible") focus(); };
    document.addEventListener("visibilitychange", visible);
    void load();
    return () => { dead = true; stop(); window.removeEventListener("focus", focus); document.removeEventListener("visibilitychange", visible); };
  });
  return root;
}
